/**
 * Date helpers for the field details modal (timeline keys, chart axis labels).
 */

/** Returns local calendar date as `yyyy-MM-dd` (used for API filters and Set keys). */
export function dateToYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Canonical day key (`yyyy-MM-dd`) for matching satellite products and anomalies.
 *
 * Backend dates may carry a time-of-day / timezone (e.g. `2024-05-15T22:00:00Z`) while
 * anomalies are date-only (`2024-05-15`). Parsing both with `new Date()` and reading the
 * *local* calendar day would shift late-day acquisitions to the next day in +UTC zones,
 * breaking the timeline ↔ anomaly match. We therefore use the literal `yyyy-MM-dd` prefix
 * of the stored string (no timezone math), falling back to the local day only when the
 * value is not an ISO date string.
 */
export function ymdFromApiDate(raw: unknown): string | null {
  if (raw instanceof Date) {
    return isNaN(raw.getTime()) ? null : dateToYmd(raw);
  }
  if (typeof raw === 'string') {
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      return `${m[1]}-${m[2]}-${m[3]}`;
    }
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : dateToYmd(parsed);
  }
  return null;
}

/** Builds a local-midnight {@link Date} from a `yyyy-MM-dd` key (so `dateToYmd` round-trips). */
export function ymdToLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

/**
 * Formats an ISO date string (or parseable date) for compact chart/timeline labels: `dd.MM.yy`.
 */
export function formatDateLabelShortYear(raw: string): string {
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) {
    return String(raw);
  }
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const yyyy = String(dt.getFullYear());
  return `${dd}.${mm}.${yyyy.slice(-2)}`;
}
